import { ReactComponent as CloseIcon } from "@securitize/assets-sec-ui/dist/icons/svg/exit.svg";
import {
  useMediaQuery,
  WhmDialog,
  WhmDialogActions,
  WhmDialogContent,
  WhmDialogTitle,
  WhmIconButton,
  whmTheme,
} from "@securitize/reactjs-whm";
import { Form, Formik } from "formik";
import { useContext } from "react";
import * as Yup from "yup";
import { I18nContext } from "../../contexts/I18nContext";
import TranslationKeys, {
  TranslationKey,
} from "../../contexts/I18nContext/translationKeys";
import { ResponseError } from "../../data/types/responseError";
import { useCreateEntityMutation } from "../../data/useCreateEntityMutation";
import { allowedEntityNameCharsRegex } from "../../utils/validations";
import { Translate, TranslateHtml } from "../Translation/Translate";
import { CountriesProvider } from "../shared/CountryTextField/CountriesContext";
import ErrorSnackbar from "../shared/ErrorSnackbar";
import Snackbar, {
  SnackbarContext,
  SnackbarProvider,
} from "../shared/Snackbar";
import {
  CreateEntityForm,
  CreateEntityFormValues,
  SubmitButton,
} from "./CreateEntityForm";

interface CreateEntityModalProps {
  open: boolean;
  onClose: () => void;
  onSwitchUser: (investorId: string) => Promise<void>;
}

const createValidationSchema = (
  getTranslation: (key: TranslationKey) => string,
) =>
  Yup.object().shape({
    entityName: Yup.string()
      .required(
        getTranslation(
          TranslationKeys.REGISTRATION_VALIDATION_REQUIRED_ENTITY_NAME,
        ),
      )
      .matches(
        allowedEntityNameCharsRegex,
        getTranslation(
          TranslationKeys.REGISTRATION_VALIDATION_INVALID_ENTITY_NAME,
        ),
      )
      .min(
        2,
        getTranslation(TranslationKeys.REGISTRATION_VALIDATION_MINIMUM_LENGTH),
      )
      .max(
        32,
        getTranslation(TranslationKeys.REGISTRATION_VALIDATION_MAXIMUM_LENGTH),
      ),
    entityCountry: Yup.object().required(
      getTranslation(
        TranslationKeys.REGISTRATION_VALIDATION_REQUIRED_ENTITY_COUNTRY,
      ),
    ),
    entityState: Yup.object().when("entityCountry", {
      is: (val: { value: string } | null) => val?.value === "US",
      then: (schema) =>
        schema.required(
          getTranslation(
            TranslationKeys.REGISTRATION_VALIDATION_REQUIRED_ENTITY_STATE,
          ),
        ),
      otherwise: (schema) => schema.nullable(),
    }),
  });

function CreateEntityModalContent({
  open,
  onClose,
  onSwitchUser,
}: CreateEntityModalProps) {
  const isMobile = useMediaQuery(whmTheme.breakpoints.down("xl"));
  const { getTranslation } = useContext(I18nContext);
  const { showSnackbar } = useContext(SnackbarContext);
  const createEntityMutation = useCreateEntityMutation();

  const validationSchema = createValidationSchema(getTranslation);

  const handleSubmit = async (values: CreateEntityFormValues) => {
    try {
      const data = await createEntityMutation.mutateAsync({
        companyName: values.entityName,
        countryCode: values.entityCountry?.value ?? "",
        countryState: values.entityState?.value,
      });
      await onSwitchUser(data.investorId);
      window.location.assign(
        data.editableProfileEnabled
          ? "/profile"
          : "/profile/verification/entity-information",
      );
    } catch (error) {
      if (error instanceof ResponseError) {
        const isQuotaExceeded =
          error.body?.details?.errorCode === "ENTITY_QUOTA_EXCEEDED";
        if (isQuotaExceeded) {
          showSnackbar({
            severity: "error",
            message: (
              <TranslateHtml
                translationKey={TranslationKeys.CREATE_ENTITY_QUOTA_EXCEEDED}
              />
            ),
          });
          onClose();
        } else {
          showSnackbar({
            severity: "error",
            message: <ErrorSnackbar error={error} />,
          });
        }
      } else {
        showSnackbar({
          severity: "error",
          message: <ErrorSnackbar />,
        });
      }
    }
  };

  return (
    <WhmDialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="xs"
      fullWidth
    >
      <CountriesProvider>
        <Formik<CreateEntityFormValues>
          initialValues={{
            entityName: "",
            entityCountry: null,
            entityState: null,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          <Form>
            <WhmDialogTitle
              variant="h4"
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Translate
                translationKey={TranslationKeys.CREATE_ENTITY_MODAL_TITLE}
              />
              <WhmIconButton onClick={onClose}>
                <CloseIcon />
              </WhmIconButton>
            </WhmDialogTitle>
            <WhmDialogContent>
              <CreateEntityForm />
            </WhmDialogContent>
            <WhmDialogActions>
              <SubmitButton />
            </WhmDialogActions>
          </Form>
        </Formik>
      </CountriesProvider>
    </WhmDialog>
  );
}

export default function CreateEntityModal(props: CreateEntityModalProps) {
  return (
    <SnackbarProvider>
      <CreateEntityModalContent {...props} />
      <Snackbar />
    </SnackbarProvider>
  );
}
